import {
  useQuery,
  useMutation,
  UseQueryOptions,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import apiClient from '../client';
import {
  UserProfileView,
  UserProfileInput,
  UserPublicProfileView,
  ApiError,
} from '../types';
import { useAuth } from '@/contexts/AuthContext';

export const profileKeys = {
  all: ['profile'] as const,
  me: () => [...profileKeys.all, 'me'] as const,
  public: (userId: string) => [...profileKeys.all, 'public', userId] as const,
};

export const useMyProfile = (
  options?: Omit<
    UseQueryOptions<UserProfileView, ApiError>,
    'queryKey' | 'queryFn'
  >,
) => {
  const { isAuthenticated } = useAuth();
  
  return useQuery<UserProfileView, ApiError>({
    queryKey: profileKeys.me(),
    queryFn: async () => {
      const response = await apiClient.get<UserProfileView>(
        '/users/me/profile',
      );
      return response.data;
    },
    enabled: isAuthenticated && (options?.enabled !== false),
    ...options,
  });
};

export const useUpdateProfile = (
  options?: UseMutationOptions<UserProfileView, ApiError, UserProfileInput>,
) => {
  const queryClient = useQueryClient();

  return useMutation<UserProfileView, ApiError, UserProfileInput>({
    mutationFn: async (data: UserProfileInput) => {
      const response = await apiClient.put<UserProfileView>(
        '/users/me/profile',
        data,
      );
      return response.data;
    },
    onSuccess: (data: UserProfileView) => {
      queryClient.setQueryData(profileKeys.me(), data);
    },
    ...options,
  });
};

export const usePublicProfile = (
  userId: string,
  options?: Omit<
    UseQueryOptions<UserPublicProfileView, ApiError>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<UserPublicProfileView, ApiError>({
    queryKey: profileKeys.public(userId),
    queryFn: async () => {
      const response = await apiClient.get<UserPublicProfileView>(
        `/users/${userId}/profile`,
      );
      return response.data;
    },
    enabled: !!userId,
    ...options,
  });
};
